/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import * as Icons from '@mui/icons-material';

HeatmapModule(Highcharts);

const ALLOWED_TYPES = new Set([
  'table',
  'bar_chart',
  'line_chart',
  'area_chart',
  'percent_area_chart',
  'combo_chart',
  'cohort'
]);

const formatValue = (value, format = 'number', locale = 'en-US') => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value !== 'number') return value;

  if (format === 'percentage') return `${Math.round(value)}%`;
  if (format === 'currency') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  }

  if (format === 'rev') {
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0
    }).format(value)} REV`;
  }

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0
  }).format(value);
};

export const normalizeAssistantArtifact = artifact => {
  if (!artifact || !ALLOWED_TYPES.has(artifact.type)) return null;

  if (artifact.chartSpec) {
    const spec = artifact.chartSpec;
    const validPercentArea =
      artifact.type === 'percent_area_chart' &&
      spec.version === 1 &&
      spec.kind === 'percent_area' &&
      spec.xAxis?.semantic === 'time' &&
      spec.xAxis?.grain === 'month' &&
      Array.isArray(spec.xAxis?.categories) &&
      spec.yAxis?.semantic === 'percentage' &&
      spec.yAxis?.min === 0 &&
      spec.yAxis?.max === 100 &&
      spec.stacking === 'percent' &&
      Array.isArray(spec.series) &&
      spec.series.length >= 2 &&
      spec.series.every(
        series =>
          typeof series.name === 'string' &&
          Array.isArray(series.data) &&
          series.data.length === spec.xAxis.categories.length
      );
    if (!validPercentArea) return null;
  }

  if (
    artifact.type === 'table' &&
    (!Array.isArray(artifact.columns) || !Array.isArray(artifact.rows))
  ) {
    return null;
  }

  if (
    [
      'bar_chart',
      'line_chart',
      'area_chart',
      'percent_area_chart',
      'combo_chart'
    ].includes(artifact.type) &&
    (!Array.isArray(artifact.xAxis?.categories) ||
      !Array.isArray(artifact.series))
  ) {
    return null;
  }

  if (
    artifact.type === 'cohort' &&
    (!Array.isArray(artifact.xCategories) ||
      !Array.isArray(artifact.yCategories) ||
      !Array.isArray(artifact.cells))
  ) {
    return null;
  }

  return artifact;
};

const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(
    () =>
      (typeof document !== 'undefined' &&
        document.body.classList.contains('dark')) ||
      false
  );

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };
    syncDarkMode();
    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  return isDarkMode;
};

const ArtifactShell = ({ artifact, children }) => (
  <section className="overflow-hidden rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
    <header className="flex items-start justify-between gap-3 border-b border-stroke px-4 py-3 dark:border-strokedark">
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-black dark:text-white">
          {artifact.title}
        </h3>
        {artifact.description && (
          <p className="mt-1 text-xs text-body dark:text-bodydark">
            {artifact.description}
          </p>
        )}
      </div>
      <span className="shrink-0 rounded border border-stroke px-2 py-1 text-[11px] font-medium text-body dark:border-strokedark dark:text-bodydark">
        {artifact.type.replace(/_/g, ' ')}
      </span>
    </header>
    <div className="p-4">{children}</div>
    {(artifact.sourceScreenKey || artifact.valueFormat) && (
      <footer className="flex flex-wrap gap-x-3 gap-y-1 border-t border-stroke px-4 py-2 text-[11px] text-body dark:border-strokedark dark:text-bodydark">
        {artifact.sourceScreenKey && (
          <span>
            {artifact.locale === 'tr-TR' ? 'Kaynak' : 'Source'}:{' '}
            {artifact.sourceScreenKey.replace(/_/g, ' ')}
          </span>
        )}
        {artifact.valueFormat && (
          <span>
            {artifact.locale === 'tr-TR' ? 'Birim' : 'Unit'}:{' '}
            {artifact.valueFormat === 'currency'
              ? 'USD'
              : artifact.valueFormat === 'percentage'
                ? '%'
                : artifact.valueFormat === 'rev'
                  ? 'REV'
                  : artifact.valueFormat}
          </span>
        )}
      </footer>
    )}
  </section>
);

const TableArtifact = ({ artifact }) => (
  <ArtifactShell artifact={artifact}>
    <div className="max-h-96 overflow-auto">
      <table className="min-w-full border-collapse text-left text-sm">
        <thead className="sticky top-0 z-10 bg-gray-2 dark:bg-boxdark-2">
          <tr>
            {artifact.columns.map(column => (
              <th
                key={column.key}
                className="whitespace-nowrap border-b border-stroke px-3 py-2 text-xs font-semibold uppercase tracking-normal text-body dark:border-strokedark dark:text-bodydark"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {artifact.rows.map((row, rowIndex) => (
            <tr
              key={`${artifact.id}-row-${rowIndex}`}
              className="border-b border-stroke/70 last:border-0 dark:border-strokedark"
            >
              {artifact.columns.map(column => (
                <td
                  key={`${artifact.id}-${rowIndex}-${column.key}`}
                  className="max-w-xs px-3 py-2 align-top text-black dark:text-white"
                >
                  <span className="line-clamp-3 break-words">
                    {formatValue(
                      row[column.key],
                      column.valueFormat || column.type,
                      artifact.locale
                    )}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {artifact.summary && (
      <p className="mt-3 text-xs text-body dark:text-bodydark">
        {artifact.summary}
      </p>
    )}
  </ArtifactShell>
);

const ChartArtifact = ({ artifact, isDarkMode }) => {
  const [showDetails, setShowDetails] = useState(false);
  const options = useMemo(() => {
    const chartSpec = artifact.chartSpec;
    const categories =
      chartSpec?.xAxis?.categories || artifact.xAxis.categories;
    const chartSeries = chartSpec?.series || artifact.series;
    let baseType = 'line';
    if (artifact.type === 'bar_chart') {
      baseType = 'bar';
    } else if (
      artifact.type === 'area_chart' ||
      artifact.type === 'percent_area_chart'
    ) {
      baseType = 'area';
    } else if (artifact.type === 'combo_chart') {
      baseType = 'column';
    }
    const isPercentArea = artifact.type === 'percent_area_chart';
    const stacking = ['normal', 'percent'].includes(artifact.stacking)
      ? artifact.stacking
      : isPercentArea
        ? 'percent'
        : undefined;
    const chartType =
      artifact.orientation === 'vertical' && baseType === 'bar'
        ? 'column'
        : baseType;

    return {
      chart: {
        type: chartType,
        height: 320,
        backgroundColor: 'transparent'
      },
      accessibility: {
        description:
          artifact.description ||
          `${artifact.title}. ${artifact.yAxis?.label || ''}`
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        categories,
        title: { text: artifact.xAxis.label || '' },
        labels: {
          style: { color: isDarkMode ? '#cbd5e1' : '#475569' }
        }
      },
      yAxis: {
        min: chartSpec?.yAxis?.min ?? 0,
        ...(isPercentArea ? { max: chartSpec?.yAxis?.max ?? 100 } : {}),
        title: {
          text: artifact.yAxis?.label || '',
          style: { color: isDarkMode ? '#cbd5e1' : '#475569' }
        },
        labels: {
          style: { color: isDarkMode ? '#cbd5e1' : '#475569' },
          formatter: function () {
            return formatValue(
              Number(this.value),
              artifact.valueFormat,
              artifact.locale
            );
          }
        },
        gridLineColor: isDarkMode ? '#334155' : '#e2e8f0'
      },
      legend: {
        itemStyle: { color: isDarkMode ? '#e2e8f0' : '#1f2937' }
      },
      tooltip: {
        shared: true,
        valueDecimals: 0,
        valuePrefix: artifact.valueFormat === 'currency' ? '$' : '',
        valueSuffix:
          artifact.valueFormat === 'percentage' || isPercentArea
            ? '%'
            : artifact.valueFormat === 'rev'
              ? ' REV'
              : artifact.valueFormat === 'currency'
                ? ' USD'
                : ''
      },
      plotOptions: {
        series: {
          borderRadius: 3,
          marker: { enabled: categories.length <= 24 },
          ...(stacking ? { stacking } : {})
        },
        ...(isPercentArea
          ? {
              area: {
                stacking: 'percent'
              }
            }
          : {})
      },
      series: chartSeries.map(series => ({
        name: series.name,
        type:
          artifact.type === 'combo_chart' ? series.type || 'column' : baseType,
        data: series.data
      }))
    };
  }, [artifact, isDarkMode]);

  return (
    <ArtifactShell artifact={artifact}>
      <HighchartsReact highcharts={Highcharts} options={options} />
      {artifact.detailTable && (
        <div className="mt-3 border-t border-stroke pt-3 dark:border-strokedark">
          <button
            type="button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => setShowDetails(current => !current)}
            aria-expanded={showDetails}
          >
            {showDetails ? 'Detayları gizle' : 'Detayları göster'}
          </button>
          {showDetails && (
            <div className="mt-3 max-h-72 overflow-auto">
              <table className="min-w-full border-collapse text-left text-xs">
                <thead className="sticky top-0 bg-gray-2 dark:bg-boxdark-2">
                  <tr>
                    {artifact.detailTable.columns.map(column => (
                      <th
                        key={column.key}
                        className="whitespace-nowrap border-b border-stroke px-2 py-2 font-semibold dark:border-strokedark"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {artifact.detailTable.rows.map((row, rowIndex) => (
                    <tr
                      key={`${artifact.id}-detail-${rowIndex}`}
                      className="border-b border-stroke/70 last:border-0 dark:border-strokedark"
                    >
                      {artifact.detailTable.columns.map(column => (
                        <td key={column.key} className="px-2 py-2">
                          {formatValue(
                            row[column.key],
                            column.valueFormat || column.type,
                            artifact.locale
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {artifact.detailTable.summary && (
                <p className="mt-2 text-xs text-body dark:text-bodydark">
                  {artifact.detailTable.summary}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </ArtifactShell>
  );
};

const CohortArtifact = ({ artifact, isDarkMode }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: 'heatmap',
        height: 360,
        backgroundColor: 'transparent'
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        categories: artifact.xCategories,
        opposite: true,
        labels: {
          style: { color: isDarkMode ? '#cbd5e1' : '#475569' }
        }
      },
      yAxis: {
        categories: artifact.yCategories,
        reversed: true,
        title: null,
        labels: {
          style: { color: isDarkMode ? '#cbd5e1' : '#475569' }
        }
      },
      colorAxis: {
        min: 0,
        max: 100,
        stops: isDarkMode
          ? [
              [0, '#1e293b'],
              [0.5, '#0e7490'],
              [1, '#22c55e']
            ]
          : [
              [0, '#f1f5f9'],
              [0.5, '#38bdf8'],
              [1, '#16a34a']
            ]
      },
      tooltip: {
        formatter: function () {
          const xLabel = artifact.xCategories[this.point.x] || '';
          const yLabel = artifact.yCategories[this.point.y] || '';
          return `<b>${yLabel}</b><br/>${xLabel}: <b>${Math.round(
            this.point.value || 0
          )}%</b>`;
        }
      },
      series: [
        {
          borderWidth: 1,
          borderColor: isDarkMode ? '#334155' : '#ffffff',
          data: artifact.cells.map(cell => [
            Number(cell.x || 0),
            Number(cell.y || 0),
            Number(cell.value || 0)
          ]),
          dataLabels: {
            enabled: true,
            color: isDarkMode ? '#f8fafc' : '#0f172a',
            formatter: function () {
              return `${Math.round(this.point.value || 0)}%`;
            }
          }
        }
      ]
    }),
    [artifact, isDarkMode]
  );

  return (
    <ArtifactShell artifact={artifact}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </ArtifactShell>
  );
};

const InvalidArtifact = () => (
  <div className="flex items-center gap-2 rounded-lg border border-stroke bg-white px-4 py-3 text-sm text-body dark:border-strokedark dark:bg-boxdark dark:text-bodydark">
    <Icons.InfoOutlined fontSize="small" />
    This artifact could not be displayed.
  </div>
);

const AssistantArtifactRenderer = ({ artifact }) => {
  const normalizedArtifact = normalizeAssistantArtifact(artifact);
  const isDarkMode = useDarkMode();

  if (!normalizedArtifact) return <InvalidArtifact />;

  if (normalizedArtifact.type === 'table') {
    return <TableArtifact artifact={normalizedArtifact} />;
  }

  if (normalizedArtifact.type === 'cohort') {
    return (
      <CohortArtifact artifact={normalizedArtifact} isDarkMode={isDarkMode} />
    );
  }

  return (
    <ChartArtifact artifact={normalizedArtifact} isDarkMode={isDarkMode} />
  );
};

export default AssistantArtifactRenderer;
